import { Injectable } from '@angular/core';
import { MessageService } from '@sla-ng/core';
import { FieldType } from '@sla-ng/ui-material';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { IContributionMarginLevel } from '../../../models/contribution-margin-level.model';
import { ContributionMarginLevelResource } from '../../../resources/contribution-margin-level.resource';
import { IContributionMarginLevelState } from './contribution-margin-level-state.model';

let _state: IContributionMarginLevelState = {
    columns: [ 'name', 'order', 'edit-button' ],
    data: [],
    dialogFields: [
        { field: 'name', label: 'app.model.contribution-margin-level.name.label', type: FieldType.text, required: true },
        { field: 'order', label: 'app.model.contribution-margin-level.order.label', type: FieldType.number, required: true },
    ],
    isLoading: false,
};

@Injectable({
    providedIn: 'root',
})
export class ContributionMarginLevelFacade {

    protected store$: BehaviorSubject<IContributionMarginLevelState> = new BehaviorSubject<IContributionMarginLevelState>(_state);

    constructor(private myContributionMarginLevelResource: ContributionMarginLevelResource,
                private myMessageService: MessageService) {
    }

    public async fetchData(): Promise<void> {
        this.setIsloading(true);
        const data: IContributionMarginLevel[] = await this.myContributionMarginLevelResource.getAllContributionMarginLevels().toPromise().catch(() => []);
        this.updateState({ ..._state, data });
        this.setIsloading(false);
    }

    public async addContributionMarginLevel(contributionMarginLevel: IContributionMarginLevel): Promise<void> {
        const createdContributionMarginLevel: IContributionMarginLevel = await this.myContributionMarginLevelResource
            .addContributionMarginLevel(contributionMarginLevel).toPromise().catch(() => null);
        if (createdContributionMarginLevel !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.contribution-margin-level.actions.added.label');
        }
    }

    public async editContributionMarginLevel(contributionMarginLevel: IContributionMarginLevel): Promise<void> {
        const updatedContributionMarginLevel: IContributionMarginLevel = await this.myContributionMarginLevelResource
            .updateContributionMarginLevel(contributionMarginLevel).toPromise();
        if (updatedContributionMarginLevel !== null){
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.contribution-margin-level.actions.edited.label');
        }
    }

    public subState(): Observable<IContributionMarginLevelState> {
        return this.store$.asObservable();
    }

    public async deleteContributionMarginLevel(contributionMarginLevelId: number): Promise<void> {
        this.setIsloading(true);
        try {
            await firstValueFrom(this.myContributionMarginLevelResource.deleteContributionMarginLevel(contributionMarginLevelId));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.contribution-margin-level.actions.deleted.label');
            await this.fetchData();
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    public clearState(): void {
        this.updateState({ ..._state, data: [] });
    }

    private updateState(state: IContributionMarginLevelState): void {
        this.store$.next(_state = state);
    }

    private setIsloading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }
}
